import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DialogControl, FieldControl } from 'spark-core-dx/components';
import { SystemTypes } from 'spark-core-dx/services';
import { useCoreContext, useCoreStateContext } from 'spark-core-dx/contexts';
import { useGenericManager } from 'spark-core-dx/hooks';
import { ItemServiceFactory } from '../../../../services/ItemServiceFactory';
import { useDashboardDomainContext } from '../../DashboardDomainProvider';

const unwrapServiceResult = (result) => (Array.isArray(result) ? result?.[0] : result) ?? null;

const normalizeValue = (value) => {
  if (Array.isArray(value)) {
    return normalizeValue(value[0]);
  }

  if (value && typeof value === 'object') {
    return String(value?._id ?? value?.UniqueId ?? value?.Value ?? value?.value ?? value?.Id ?? '').trim();
  }

  return String(value ?? '').trim();
};

const getFirstItem = (result) => {
  const normalized = unwrapServiceResult(result);
  if (Array.isArray(normalized?.Items) && normalized.Items.length > 0) {
    return normalized.Items[0];
  }

  return normalized?.Item ?? null;
};

const getValidationDisplayMessages = (result) =>
  Object.values(unwrapServiceResult(result)?.ValidationErrors ?? {})
    .map((entry) => String(entry?.DisplayMessage ?? entry?.Content ?? '').trim())
    .filter(Boolean);

const formatDate = (value) => {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  }).format(parsed);
};

const buildWorkspaceOptionText = (workspace, { isActive = false } = {}) => {
  const name = String(workspace?.Name ?? 'Model Workspace').trim();
  const version = String(workspace?.Version ?? '').trim();
  const parts = [`${name}${version ? ` v${version}` : ''}`];
  const publishedDate = formatDate(workspace?.PublishedDateTime);
  const status = Number(workspace?.Status ?? 0);

  if (status === 1) {
    parts.push('Current Draft');
  } else if (status === 2) {
    parts.push(publishedDate ? `Published ${publishedDate}` : 'Published');
  }

  if (isActive) {
    parts.push('(Active)');
  }

  return parts.join(' | ');
};

const MigrateDashboardModelWorkspaceDialog = ({ isActive, closeDialog }) => {
  const { activeDashboard, dashboardLib, switchMode } = useDashboardDomainContext();
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const workspaceMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModelWorkspace);
  const workspaceService = useMemo(
    () => itemServiceFactory.GetItemService(itemServiceFactory.ItemServiceEnum.DataModelWorkspace, coreState, coreContext, true),
    [coreContext, coreState, itemServiceFactory]
  );
  const workspaceMgrRef = useRef(workspaceMgr);
  const workspaceServiceRef = useRef(workspaceService);

  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [draft, setDraft] = useState({
    targetPublishedDataModelWorkspaceId: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const currentDraftWorkspaceId = normalizeValue(activeDashboard?.DataModelWorkspaceId);
  const currentPublishedWorkspaceId = normalizeValue(activeDashboard?.PublishedDataModelWorkspaceId);

  useEffect(() => {
    workspaceMgrRef.current = workspaceMgr;
  }, [workspaceMgr]);

  useEffect(() => {
    workspaceServiceRef.current = workspaceService;
  }, [workspaceService]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    setDraft({
      targetPublishedDataModelWorkspaceId: '',
    });
    setError('');

    let cancelled = false;

    const loadWorkspaceOptions = async () => {
      const lineageWorkspaceId = currentDraftWorkspaceId || currentPublishedWorkspaceId;
      if (!lineageWorkspaceId) {
        if (!cancelled) {
          setWorkspaceOptions([]);
          setError('This dashboard is not linked to a model workspace.');
        }
        return;
      }

      setIsLoading(true);

      try {
        const currentWorkspaceResult = await workspaceServiceRef.current?.single?.(lineageWorkspaceId);
        const currentWorkspace = getFirstItem(currentWorkspaceResult);
        const masterWorkspaceId = normalizeValue(
          currentWorkspace?.MasterDataModelWorkspaceId ?? currentWorkspace?._id ?? currentWorkspace?.UniqueId
        );

        if (!masterWorkspaceId) {
          if (!cancelled) {
            setWorkspaceOptions([]);
            setError('Unable to determine the workspace lineage for migration.');
          }
          return;
        }

        const result = await workspaceMgrRef.current?.Query?.({
          model: 'x => x.IsActive == true && x.IsDeleted == false && x.MasterDataModelWorkspaceId == masterWorkspaceId && (x.Status == draftStatus || x.Status == publishedStatus)',
          params: {
            masterWorkspaceId,
            draftStatus: 1,
            publishedStatus: 2,
          },
        });

        const nextOptions = (Array.isArray(result?.Items) ? result.Items : [])
          .sort((left, right) => {
            const leftStatus = Number(left?.Status ?? 0);
            const rightStatus = Number(right?.Status ?? 0);
            if (leftStatus !== rightStatus) {
              return leftStatus - rightStatus;
            }

            const leftDate = left?.PublishedDateTime ? Date.parse(left.PublishedDateTime) : 0;
            const rightDate = right?.PublishedDateTime ? Date.parse(right.PublishedDateTime) : 0;
            if (rightDate !== leftDate) {
              return rightDate - leftDate;
            }

            return String(right?.Version ?? '').localeCompare(String(left?.Version ?? ''), undefined, {
              numeric: true,
              sensitivity: 'base',
            });
          })
          .map((workspace) => {
            const workspaceId = normalizeValue(workspace?._id ?? workspace?.UniqueId);
            const isActiveOption = workspaceId === currentDraftWorkspaceId;

            return {
              Text: buildWorkspaceOptionText(workspace, { isActive: isActiveOption }),
              Value: workspaceId,
              Disabled: isActiveOption,
            };
          })
          .filter((option) => !!option.Value);

        const firstEnabledOption = nextOptions.find((option) => option.Disabled !== true);

        if (cancelled) {
          return;
        }

        setWorkspaceOptions(nextOptions);
        setDraft((prev) => ({
          ...prev,
          targetPublishedDataModelWorkspaceId: prev.targetPublishedDataModelWorkspaceId
            || firstEnabledOption?.Value
            || '',
        }));

        if (nextOptions.length === 0) {
          setError('No draft or published model workspace versions are available for this dashboard lineage.');
        } else if (!firstEnabledOption) {
          setError('This dashboard is already using the only available model workspace version.');
        }
      } catch (loadError) {
        console.error('Dashboard model workspace migration options load failed:', loadError);
        if (!cancelled) {
          setWorkspaceOptions([]);
          setError('Unable to load model workspace versions.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadWorkspaceOptions();

    return () => {
      cancelled = true;
    };
  }, [currentDraftWorkspaceId, currentPublishedWorkspaceId, isActive]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setError('');

    try {
      const result = await dashboardLib?.migrateDashboardModelWorkspace?.({
        targetPublishedDataModelWorkspaceId: draft.targetPublishedDataModelWorkspaceId,
      });

      if (result?.Success) {
        closeDialog();
        switchMode?.('design');
        return;
      }

      const validationMessages = getValidationDisplayMessages(result);
      setError(
        validationMessages[0]
        || result?.DisplayMessage
        || result?.Message?.DisplayMessage
        || result?.Message?.Content
        || 'Unable to migrate the dashboard model workspace.'
      );
    } catch (saveError) {
      console.error('Dashboard model workspace migration failed:', saveError);
      setError(String(saveError?.message ?? 'Unable to migrate the dashboard model workspace.'));
    } finally {
      setIsSaving(false);
    }
  }, [closeDialog, dashboardLib, draft.targetPublishedDataModelWorkspaceId, switchMode]);

  return (
    <DialogControl
      className="dashboard-dialog"
      openDialog={isActive}
      title="Migrate Dashboard Model Workspace"
      disableOk={
        isLoading
        || isSaving
        || !draft.targetPublishedDataModelWorkspaceId
        || workspaceOptions.length === 0
      }
      onOk={handleSave}
      onCancel={() => {
        closeDialog();
        setError('');
      }}
      okText="Migrate"
    >
      <div className="dialog-text">
        <div>
          This updates the dashboard draft only. The current published dashboard stays unchanged until you publish again.
        </div>
        <div>
          Migration always starts from the current dashboard draft.
        </div>
        <FieldControl
          key={`migrate-dashboard-workspace:${workspaceOptions.length}:${draft.targetPublishedDataModelWorkspaceId}`}
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.SearchSelectList,
            FieldName: 'TargetPublishedDataModelWorkspaceId',
            DisplayName: 'Model Workspace Version',
            Description: 'Choose the current draft or current published model workspace version that this dashboard draft should use.',
            DataJson: workspaceOptions,
            IsRequired: true,
          }}
          groupId="dashboard-migration"
          value={draft.targetPublishedDataModelWorkspaceId}
          onChange={(value) => {
            setDraft((prev) => ({
              ...prev,
              targetPublishedDataModelWorkspaceId: normalizeValue(value),
            }));
          }}
        />
        {error ? (
          <div className="dialog-text" style={{ color: 'var(--error)' }}>
            {error}
          </div>
        ) : null}
      </div>
    </DialogControl>
  );
};

export default MigrateDashboardModelWorkspaceDialog;
